import { useCallback, useRef, useMemo, useState } from 'react';
import { Button, Space, Tag, Modal, message, Select, Alert } from 'antd';
import {
  ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getWebhookEvents,
  retryWebhookEvent,
  WEBHOOK_EVENT_STATUSES,
  WEBHOOK_EVENT_TYPES,
  type WebhookEventInfo,
  type WebhookEventFilters,
} from '@/api/integrationApi';
import { useAuth } from '@/hooks/useAuth';
import IntegrationNav from './IntegrationNav';

export default function WebhookEventListPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [eventTypeFilter, setEventTypeFilter] = useState<string | undefined>();

  const canRead = hasPermission('admin', 'read');
  const canWrite = hasPermission('admin', 'write');

  const fetchData = useCallback(
    (params: SearchParams) => {
      const filters: WebhookEventFilters = { ...params };
      if (statusFilter) filters.status = statusFilter;
      if (eventTypeFilter) filters.event_type = eventTypeFilter;
      return getWebhookEvents(filters);
    },
    [statusFilter, eventTypeFilter]
  );

  const handleRetry = useCallback(
    async (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('webhook.confirm_retry_event'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        onOk: async () => {
          try {
            await retryWebhookEvent(id);
            message.success(t('webhook.event_retry_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('webhook.event_retry_failed'));
          }
        },
      });
    },
    [t]
  );

  const statusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'green';
      case 'failed': return 'red';
      case 'retrying': return 'orange';
      case 'pending': return 'blue';
      default: return 'default';
    }
  };

  const columns: ColumnsType<WebhookEventInfo> = useMemo(
    () => [
      {
        title: t('webhook.event_field.event_type'),
        dataIndex: 'event_type',
        key: 'event_type',
        width: 180,
        ellipsis: true,
      },
      {
        title: t('webhook.event_field.target_url'),
        dataIndex: 'target_url',
        key: 'target_url',
        width: 200,
        ellipsis: true,
      },
      {
        title: t('webhook.event_field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (st: string) => (
          <Tag color={statusColor(st)}>{t(`webhook.event_status.${st}`, st)}</Tag>
        ),
      },
      {
        title: t('webhook.event_field.http_status_code'),
        dataIndex: 'http_status_code',
        key: 'http_status_code',
        width: 80,
        render: (v: number | null) => v ?? '-',
      },
      {
        title: t('webhook.event_field.attempt_count'),
        dataIndex: 'attempt_count',
        key: 'attempt_count',
        width: 80,
        render: (v: number, record: WebhookEventInfo) => `${v}/${record.max_attempts}`,
      },
      {
        title: t('webhook.event_field.delivered_at'),
        dataIndex: 'delivered_at',
        key: 'delivered_at',
        width: 170,
        render: (v: string) => v ? new Date(v).toLocaleString() : '-',
      },
      {
        title: t('webhook.event_field.error_message'),
        dataIndex: 'error_message',
        key: 'error_message',
        width: 200,
        ellipsis: true,
        render: (v: string) => v || '-',
      },
      {
        title: t('app.actions'),
        key: 'col_actions',
        width: 80,
        render: (_: unknown, record: WebhookEventInfo) => (
          <Space size="small">
            {canWrite && (record.status === 'failed' || record.status === 'retrying') && (
              <Button
                type="link"
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => handleRetry(record.id)}
                title={t('webhook.retry_event')}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, canWrite, handleRetry]
  );

  const toolbar = (
    <Space>
      <Select
        allowClear
        placeholder={t('webhook.event_field.status')}
        value={statusFilter}
        onChange={(v) => { setStatusFilter(v); gridRef.current?.reload(); }}
        style={{ width: 140 }}
        options={WEBHOOK_EVENT_STATUSES.map((st) => ({
          value: st,
          label: t(`webhook.event_status.${st}`),
        }))}
      />
      <Select
        allowClear
        placeholder={t('webhook.event_field.event_type')}
        value={eventTypeFilter}
        onChange={(v) => { setEventTypeFilter(v); gridRef.current?.reload(); }}
        style={{ width: 220 }}
        options={WEBHOOK_EVENT_TYPES.filter((et) => et !== '*').map((et) => ({
          value: et,
          label: et,
        }))}
      />
    </Space>
  );

  if (!canRead) return <Alert type="error" message={t('app.forbidden')} />;

  return (
    <div>
      <IntegrationNav />
      <h2>{t('integration.webhook_events_title')}</h2>
      <DataGrid<WebhookEventInfo>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
      />
    </div>
  );
}
